import { Fragment, useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useBackdropDismiss } from '~/hooks/use-backdrop-dismiss';
import { scrollConversationToEnd } from '~/features/chat/scroll-conversation';
import { ArrowsClockwise, Check, CircleNotch, ClipboardText, DotsThree, Eraser, Eye, EyeSlash, FloppyDisk, Info, Lightning, LinkSimple, NotePencil, Paperclip, TextAa, Trash, Warning, X } from '@phosphor-icons/react';
import { SlidersHorizontalIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useTooltipsEnabled } from '~/components/ui/tooltip';
import { useSetTooltipsEnabled } from '~/hooks/mutations/use-ui-preferences-mutations';
import { cn } from '~/lib/utils';
import { Button } from '~/components/ui/button';
import { Textarea } from '~/components/ui/textarea';
import { ActionBar } from '~/components/ui/action-bar';
import { AUTO_CLEAR_CHOICES, autoClearChoiceLabel, autoClearChoiceOf, autoClearRequestFor, isDefaultAutoClearChoice } from './auto-clear-choice';
import { useCanBuildApps } from '~/hooks/queries/use-is-admin';
import { useFeatureFlag, CODING_CAPABILITIES_FLAG, CODING_DESIGN_DIRECTION_FLAG } from '~/hooks/queries/use-feature-flags';
import { useCloseOnOperatorPanelRequest } from '~/lib/stores/use-close-on-operator-panel-request';
import { useCloseOnProBuilderOpen } from '~/lib/stores/use-close-on-pro-builder-open';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '~/components/ui/dialog';
import { MarkdownRenderer } from '~/components/markdown-renderer';
import { EddyWordmark } from '~/components/eddy-mark';
import { ChatInput } from '~/features/chat/chat-input';
import { BudgetBlockedCard } from '~/features/limits/budget-blocked-card';
import {
  READING_CARD_PX,
  READING_COLUMN_PX,
  READING_FONT_PX,
  ReadingPrefsPanel,
  useReadingPrefs,
} from '~/features/chat/reading-prefs';
import {
  useCodingSessions,
  useCodingSessionContext,
  useCodingDraftUpdate,
} from '~/hooks/queries/use-coding-sessions';
import {
  useClearCodingSession,
  useCreateCodingSession,
  useDeleteCodingSession,
  useHandoverCodingSession,
  usePublishCodingSession,
  useSaveCodingSession,
  useSetCodingAutoClear,
  useSetCodingSessionContext,
} from '~/hooks/mutations/use-coding-mutations';
import { useQueryClient } from '@tanstack/react-query';
import { codingKeys } from '~/lib/query-keys';
import { announceSavedApp } from './saved-app-toast';
import { ApiError } from '~/lib/api-client';
import { useConfirm } from '~/components/confirm-dialog';
import { buildLiveness, STUCK_LABEL, type BuildLiveness } from './build-liveness';
import { useCodingSession } from './use-coding-session';
import { DraftUpdateButton, draftUpdateGate, overlapNames } from './draft-update-button';
import { AskOwnerToAcceptButton, publishControlFor } from '~/features/apps/ask-to-accept';
import { ownerDraftLink, previewEye } from './preview-eye';
import {
  buildSessionOptions,
  buildStatusLabel,
  CODING_CONTEXT_MAX_CHARS,
  formatDuration,
  formatRunSummary,
  formatSessionDuration,
  formatSessionCost,
  formatSessionTurns,
  type CodingAppView,
  type RunSummary,
  type CodingQuestionAnswer,
  type CodingStatus,
  type RunActivity,
  type TranscriptItem,
  type UploadAttachment,
  type WaitingReason,
  appAudience,
} from './coding-events';
import { AppSharePopover } from '~/features/apps/app-share-control';
import { CodingQuestionsCard } from './coding-questions-card';
import { guidedInterviewBarState } from './guided-interview-bar';
import { GuidedInterviewBar } from './guided-interview-bar-view';
import { ProjectContextDialog } from './project-context-dialog';
import { ConfigurationDialog } from './configuration-dialog';
import { DesignDirectionDisclosure, type UploadInFlight } from './design-direction-section';
import { buildDesignCreatePayload, EMPTY_DESIGN_DRAFT, type DesignDirectionDraft } from './design-direction';
import { createGateState } from './design-direction-gate';
import { submitComposerAnswer } from './composer-answer';
import { sendFailureMessage } from './send-failure';
import { Tip } from '~/components/ui/tip';
import { HeaderNewButton, HeaderProjectSwitcher, HeaderSaveButton } from '~/components/builder-header/builder-header';
import { ContextMeter } from '~/components/builder-header/context-meter';
import { ESTIMATE_HINT } from '~/features/limits/compute-blocks';

/**
 * Zen-style coding window (FRONT-333), visually matched 1:1 to the inbox
 * chat modal: same backdrop, card, eyebrow header, reading column (its
 * width + font size are the same shared prefs as the inbox modal's "Aa"
 * popover — FRONT-1668 moved this window's own control for them into the
 * "…" menu's "Appearance" item, opening a dialog instead of a popover; the
 * inbox modal's popover is unchanged), and the same ChatInput composer
 * (text + file uploads, which land in the project's uploads folder —
 * FRONT-358). The window is a VIEW onto a background coding session —
 * closing it (×, backdrop, Esc) only detaches; the run keeps going
 * server-side, and reopening replays the tape and catches up.
 *
 * Opens on the most recent session; a header switcher jumps between
 * projects or starts a new one (one short "name your project" step).
 * `initialSessionId` (FRONT-334) pre-attaches a specific session — the Files
 * page passes it when a saved code-session document is opened, so the window
 * resumes exactly the session the document journals.
 */

export function CodingSessionModal({
  onClose,
  onCloseInPlace,
  initialSessionId,
}: {
  onClose: () => void;
  /**
   * FRONT-3003: closes without the host's return-to-origin navigation, used
   * when the Pro Builder window opens over this one (a navigation would hide
   * Pro Builder again). Only a host whose `onClose` navigates needs it; the
   * rest fall back to `onClose`.
   */
  onCloseInPlace?: () => void;
  initialSessionId?: string;
}) {
  const { data: sessions, isLoading: sessionsLoading, error: sessionsError } = useCodingSessions(true);
  const [selectedId, setSelectedId] = useState<string | null>(initialSessionId ?? null);
  const [creating, setCreating] = useState(false);

  // FRONT-1668: the "Appearance" menu item opens the Width/Font size
  // controls in a dialog. Held here, not inside the dropdown, because a
  // `DropdownMenuItem` unmounts its menu (and anything nested inside it) the
  // moment it's selected — the dialog has to be a sibling driven by state
  // that outlives the menu closing.
  const [appearanceOpen, setAppearanceOpen] = useState(false);

  // FRONT-1787: the "Project context" editor. Same sibling-of-the-menu
  // pattern (and same reason) as `appearanceOpen` above.
  const [contextOpen, setContextOpen] = useState(false);
  // FRONT-2410 / FRONT-2473: Configuration — what EDDY may use while building,
  // and what the app may use (within the workspace's rules).
  const [configOpen, setConfigOpen] = useState(false);

  // FRONT-2267: the tooltips on/off switch, in the "…" menu beside Appearance
  // — the other purely-visual preference there. This header is where tooltips
  // became irritating (five icon-only controls, each with one), so this is
  // where the switch to stop them belongs. It is app-wide and saved to the
  // account, not scoped to this window.
  // Reads the RESOLVED value off the same context the gate itself reads, not
  // the raw query: the "default to on while loading" decision belongs to the
  // app-root provider alone, and re-deriving it here was a second expression
  // of one default — the two could disagree, and the menu would then tick a
  // state the gate was not in. The optimistic write below still lands
  // instantly, because the provider feeds this context from the same cache
  // entry the mutation updates.
  const tooltipsOn = useTooltipsEnabled();
  const setTooltips = useSetTooltipsEnabled();

  const [prefs, setPrefs] = useReadingPrefs();
  const cardMax = READING_CARD_PX[prefs.width];
  const columnMax = READING_COLUMN_PX[prefs.width];
  const chatFont = READING_FONT_PX[prefs.fontSize];

  // Land on the most recent session once the list arrives; if there are no
  // sessions yet, the new-project step is the only thing to show.
  useEffect(() => {
    if (!sessions || selectedId || creating) return;
    if (sessions.length > 0) setSelectedId(sessions[0].id);
    else setCreating(true);
  }, [sessions, selectedId, creating]);

  const {
    session,
    refreshApp,
    transcript,
    status,
    displayStatus,
    displayWaitingReason,
    queueBusy,
    composerBusy,
    loadError,
    uploads,
    contextUsage,
    activity,
    runSummary,
    sessionStats,
    sessionCost,
    waitingReason,
    send,
    updateDraft,
    interrupt,
    deleteUpload,
    answerQuestions,
    blockingQuestion: openQuestion,
  } = useCodingSession(creating ? null : selectedId);

  // FRONT-2935: "Updates available" — the app has changes this draft lacks.
  const { data: draftUpdateAvailable } = useCodingDraftUpdate(creating ? null : selectedId, !creating && !!session?.app);
  const [updatingDraft, setUpdatingDraft] = useState(false);
  const draftGate = draftUpdateGate({ available: draftUpdateAvailable === true, runBusy: queueBusy, pending: updatingDraft });
  const handleUpdateDraft = useCallback(async () => {
    setUpdatingDraft(true);
    try {
      const result = await updateDraft();
      if (!result) return;
      if (result.status === 'reworking') toast(`EDDY is bringing the app's latest changes together with your work in ${overlapNames(result.files, result.more)}.`);
      else if (result.status === 'updated') toast.success("Your draft now has the app's latest changes.");
      else toast("Your draft already has the app's latest changes.");
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setUpdatingDraft(false);
    }
  }, [updateDraft]);

  // FRONT-1787: the attached project's standing context, for the transcript
  // note. One file read per open session, deliberately not folded into the
  // sessions list (a hot poll).
  const { data: contextRead } = useCodingSessionContext(creating ? null : selectedId);
  // The note renders nothing when the read failed — same as no context. The
  // EDITOR is where the difference matters (see ProjectContextDialog).
  const projectContext = contextRead?.readable ? contextRead.context : null;

  // FRONT-648 / FRONT-2884: optimistic auto-handover state, synced from the
  // session row — "off" or the threshold % it fires at.
  const serverAutoClear = autoClearChoiceOf(session);
  const [autoClearChoice, setAutoClearChoice] = useState(serverAutoClear);
  useEffect(() => {
    setAutoClearChoice(serverAutoClear);
  }, [serverAutoClear]);

  // FRONT-3084: the composer stays free during the background check (a message
  // stops it), but the actions that need an idle session still wait for it.
  const actionsBusy = queueBusy || status === 'checking';

  // FRONT-1898: the composer already frees itself the instant this fires
  // (composerBusy/queueBusy fold in the manual-stop override inside
  // use-coding-session.ts) — this toast only covers a session that is not
  // running anywhere, which used to be a silent no-op with no signal at all.
  const stopRun = useCallback(() => {
    interrupt()
      .then((interrupted) => {
        if (!interrupted) {
          toast.error(
            "Nothing to stop — this session isn't running.",
          );
        }
      })
      .catch((err) => toast.error((err as Error).message || 'Could not stop the run'));
  }, [interrupt]);

  // FRONT-358: per-file delete from the uploads strip — removes the file
  // from the session repo and (via the tape event) from the visible list.
  const handleDeleteUpload = useCallback(
    (name: string) => {
      deleteUpload(name).catch((err) =>
        toast.error((err as Error).message || 'Could not remove the file'),
      );
    },
    [deleteUpload],
  );

  // FRONT-2478: the guided interview's exit, pinned above the composer. The
  // bar hides on its own once the stop lands: the answers route returns the
  // guidedBuildStopped flag, which the session hook folds in before this
  // promise settles. The stopped tape event only comes back inline when no
  // run is active; with a live run the blocking tool appends it on its next
  // poll, so `stoppedBatchId` closes the card in the meantime (the FRONT-1667
  // optimistic stop the card used to hold itself).
  const interviewBar = useMemo(
    () =>
      guidedInterviewBarState({
        transcript,
        guidedBuildStopped: session?.guidedBuildStopped ?? false,
        runStatus: status,
      }),
    [transcript, session?.guidedBuildStopped, status],
  );
  const [stoppingInterview, setStoppingInterview] = useState(false);
  const [stoppedBatchId, setStoppedBatchId] = useState<number | null>(null);
  const handleStopInterview = useCallback(
    (batchId: number, open: boolean) => {
      setStoppingInterview(true);
      answerQuestions(batchId, { stopGuidedBuild: true })
        .then((result) => {
          if (result?.ok) setStoppedBatchId(batchId);
          // Stopped, but the continuation run could not start (capacity etc.).
          if (result?.warning) toast.warning(result.warning, { duration: 8000 });
        })
        .catch((err) => {
          // FRONT-1160 (pr-panel finding): the route flips guided build off
          // BEFORE it looks at this batch, so a 409 here (the question had
          // already moved on) can still mean the stop the user asked for
          // succeeded. Say so rather than read as "your click did nothing".
          const flagStopped =
            err instanceof ApiError &&
            (err.body as { guidedBuildStopped?: boolean } | undefined)?.guidedBuildStopped;
          if (flagStopped && !open) {
            // Between rounds there is no open card to cancel, so this 409 is
            // the expected answer: the flag is off, and EDDY's next question
            // is refused in favour of asking in plain words.
            toast.info('Guided questions are now off. EDDY will ask what to build in plain words.');
          } else if (flagStopped) {
            toast.info('Guided questions are now off for this session. This particular question had already moved on.');
          } else {
            toast.error((err as Error).message || 'Could not stop the questions');
          }
        })
        .finally(() => setStoppingInterview(false));
    },
    [answerQuestions],
  );

  // Save the session to Files as its journal document (FRONT-334).
  const save = useSaveCodingSession();
  const handleSave = useCallback(() => {
    if (!session || save.isPending) return;
    save.mutate(session.id, {
      onSuccess: (result) => {
        // FRONT-1668: the menu item reads "Save Conversation" now, so the
        // toast says the same thing — it's still a Files document under the
        // hood (session-document.ts), just described by what it holds
        // rather than where it lands.
        toast.success(
          result.updated
            ? 'Conversation updated in Files'
            : 'Conversation saved to Files — find it under the code-session badge',
        );
      },
      onError: (err) => toast.error((err as Error).message || 'Could not save the session'),
    });
  }, [session, save]);

  // Generate a handover brief (FRONT-646) — an LLM carry-forward summary stored
  // on the session; the seed for "Clear & continue". Refused mid-run server-side.
  const handover = useHandoverCodingSession();
  const handleHandover = useCallback(() => {
    if (!session || handover.isPending) return;
    handover.mutate(session.id, {
      onSuccess: (result) => {
        toast.success('Handover ready — saved for Clear & continue', {
          description: result.handover.slice(0, 200) + (result.handover.length > 200 ? '…' : ''),
          duration: 8000,
        });
      },
      onError: (err) => toast.error((err as Error).message || 'Could not generate the handover'),
    });
  }, [session, handover]);

  // Clear & continue (FRONT-647): reset Eddy's context; the next message
  // continues from the latest handover. The backend requires a handover first.
  const clear = useClearCodingSession();
  const handleClear = useCallback(() => {
    if (!session || clear.isPending) return;
    clear.mutate(session.id, {
      onSuccess: () => {
        toast.success('Context cleared — your next message continues from the handover');
      },
      onError: (err) => toast.error((err as Error).message || 'Could not clear the context'),
    });
  }, [session, clear]);

  // FRONT-2776: delete the attached project, then land on the next one (or
  // the new-project step). Selection moves explicitly: the list still holds
  // the deleted row until its refetch lands, and the auto-select effect would
  // pick it straight back.
  const confirm = useConfirm();
  const deleteSession = useDeleteCodingSession();
  const handleDelete = useCallback(async () => {
    if (!session || deleteSession.isPending) return;
    const ok = await confirm({
      title: `Delete project "${session.title}"?`,
      description: session.app
        ? 'This deletes the conversation and the project files in your workspace. The published app stays as it is. Cannot be undone.'
        : 'This deletes the conversation and the project files in your workspace. Cannot be undone.',
      confirmText: 'Delete project',
      destructive: true,
    });
    if (!ok) return;
    const deletedId = session.id;
    deleteSession.mutate(deletedId, {
      onSuccess: () => {
        const next = sessions?.find((s) => s.id !== deletedId);
        if (next) {
          setSelectedId(next.id);
        } else {
          setSelectedId(null);
          setCreating(true);
        }
        toast.success('Project deleted');
      },
      onError: (err) => toast.error((err as Error).message || 'Could not delete the project'),
    });
  }, [session, sessions, deleteSession, confirm]);

  // Auto-handover + clear toggle (FRONT-648) — optimistic flip, reverts on error.
  // FRONT-2219 / FRONT-2473: the three built-in capability groups now live
  // in Configuration → "While building" (builder-capabilities.tsx). Fallback
  // true: the server still gates what a run may call, so a flags-endpoint
  // hiccup must not hide the switches.
  const capabilitiesFlagOn = useFeatureFlag(CODING_CAPABILITIES_FLAG, true);

  const autoClear = useSetCodingAutoClear();
  const handleAutoClearChoice = useCallback(
    (choice: string) => {
      if (!session || autoClear.isPending) return;
      // FRONT-3301: the "default" entry clears the session's own pick.
      const request = autoClearRequestFor(choice, autoClearChoice, session);
      if (!request) return;
      const previous = autoClearChoice;
      setAutoClearChoice(choice);
      autoClear.mutate(
        { sessionId: session.id, ...request },
        {
          onSuccess: () =>
            toast.success(
              choice === 'off'
                ? 'Auto-handover off'
                : `Auto-handover on — past ${choice}% context EDDY saves a handover and continues fresh`,
            ),
          onError: (err) => {
            setAutoClearChoice(previous);
            toast.error((err as Error).message || 'Could not change the setting');
          },
        },
      );
    },
    [session, autoClear, autoClearChoice],
  );

  // Save the project into Apps (FRONT-336, FRONT-1668, FRONT-1775). Every
  // call — first save or re-save — now goes live (status: "live"); who can
  // actually reach it is a separate axis this call never touches
  // (visibility/workspaceShared, governed by the Share control). The one
  // exception is an app the owner explicitly Archived: a save on that
  // session refuses rather than silently reviving it (pr-panel finding,
  // `CodingPublishError` code "archived").
  const saveApp = usePublishCodingSession();
  const qc = useQueryClient();
  const handleSaveApp = useCallback(() => {
    if (!session || saveApp.isPending) return;
    saveApp.mutate(session.id, {
      onSuccess: (result) => {
        // The owner chose not to publish what the checks found.
        if (!result) return;
        announceSavedApp(qc, result);
      },
      // A refusal can be two sentences long (a page changed in chat and here too): long enough to read.
      onError: (err) => toast.error((err as Error).message || 'Could not save the app', { duration: 15000 }),
    });
  }, [session, saveApp, qc]);

  // FRONT-1668: this is a `window` keydown listener, so without a guard it
  // fires on EVERY Escape, including one meant for the Appearance dialog
  // stacked on top of this window. Two things were tried and rejected before
  // this one, both confirmed live (console-instrumented) rather than assumed:
  //
  // 1. A guard reading the `appearanceOpen` React flag. Radix's own Escape
  //    handling (`@radix-ui/react-dismissable-layer`'s `useEscapeKeydown`)
  //    listens on `document` in the CAPTURE phase and synchronously flips
  //    that flag to `false` (via `onOpenChange`) before the event reaches
  //    this BUBBLE-phase `window` listener — so the guard's own closure
  //    always saw it already `false` and did nothing.
  // 2. `if (e.defaultPrevented) return;` — the fix `inbox-chat-modal.tsx`
  //    uses for the identical class of bug (FRONT-1579): Radix calls
  //    `preventDefault()` (never `stopPropagation()`) right before
  //    dismissing the innermost layer, and reading that is immune to #1's
  //    race since it mutates the event object itself, synchronously, rather
  //    than depending on a re-render. This DID stop Escape from closing the
  //    Building Session while the Appearance dialog was open — but it
  //    introduced a NEW regression: a hover-triggered Radix Tooltip is ALSO
  //    a `DismissableLayer` and ALSO calls `preventDefault()` on Escape to
  //    dismiss itself, so this guard swallowed Escape whenever a tooltip
  //    merely happened to be showing — e.g. hovering the Close button below,
  //    whose own tooltip literally reads "Close (Esc)", so closing needed
  //    two presses instead of one.
  //
  // The fix that actually works for both cases: query the DOM for a genuine
  // open MODAL DIALOG (`role="dialog"`, which a Tooltip's `role="tooltip"`
  // never carries) — but read it in the CAPTURE phase, at `window`, which
  // runs BEFORE Radix's own document-level capture listener (capture order
  // is window → document → … → target). At that point nothing has been
  // dismissed yet, so the query reflects the TRUE pre-event state and can't
  // race the way reading it in the bubble phase did (confirmed live: the
  // same query read in the bubble phase already showed no open dialog by
  // the time it ran, for the same reason `appearanceOpen` above did).
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // FRONT-2219 QA: an open Radix menu (the Session actions dropdown,
      // role="menu") is a DismissableLayer too — Escape there must close the
      // menu only, never the whole build window. Seen live: Escape with the
      // capability switches open dropped the person back to the inbox.
      // Same selector the operator panel uses for the identical guard.
      if (
        document.querySelector(
          '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [role="menu"][data-state="open"], [role="listbox"][data-state="open"]',
        )
      )
        return;
      onClose();
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [onClose]);

  // FRONT-1991: modal viewers are mutually exclusive, never stacked
  // (CLAUDE.md). The desktop operator panel is a second full-screen
  // overlay, mounted separately in `app-layout.tsx`, that has no way to
  // close THIS window itself — this hook is the other half: it closes this
  // window the instant a "Watch desktop" click raises a request, rather
  // than sitting stacked underneath the new overlay.
  useCloseOnOperatorPanelRequest(onClose);
  // FRONT-3003: same rule for the Pro Builder window opening over this one.
  useCloseOnProBuilderOpen(onCloseInPlace ?? onClose);

  // Refresh the sessions list when this window closes, so the top bar's
  // activity board shows the session's new state immediately instead of on
  // its next poll tick. This used to also stamp "the user has seen the coding
  // window" for the composer icon's green "finished" dot (FRONT-339); that
  // icon and everything behind it went on 2026-09-01 with the Build button,
  // and the stamp went with it rather than staying as a write nobody reads.
  const queryClient = useQueryClient();
  useEffect(() => {
    return () => {
      queryClient.invalidateQueries({ queryKey: codingKeys.list() });
    };
  }, [queryClient]);

  // FRONT-2258: the state strip carried a copy-link button beside the app's
  // address. The one-row header has no room for the address itself, so the
  // ACTION moves into the "…" menu rather than being dropped — Preview opens
  // the app, this copies its link.
  //
  // Guard on the API being PRESENT, not `?.` — in an insecure context (http
  // on a LAN IP, some webviews) `navigator.clipboard` is undefined, and
  // `await navigator.clipboard?.writeText(...)` resolves to `await undefined`
  // and would falsely report a copy that never happened.
  const handleCopyAppLink = useCallback(async (url: string) => {
    if (!navigator.clipboard) {
      toast.error('Copying needs a secure (https) context');
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy the link');
    }
  }, []);

  const title = creating ? 'New project' : session?.title ?? '…';

  // FRONT-341: the switcher's value MUST always resolve to a rendered
  // option — a native <select> silently displays its first option when the
  // value is missing (stale list, reorder after a background finish, list
  // cap, Files/Apps initialSessionId), which made the dropdown claim a
  // different project than the transcript. buildSessionOptions prepends the
  // attached session when the list doesn't contain it, so the dropdown, the
  // transcript, and the send target (all driven by selectedId) stay one id.
  const sessionOptions = buildSessionOptions(
    sessions,
    creating ? null : selectedId,
    session?.title ?? null,
  );

  // Backdrop dismiss keys off where the press started (see use-backdrop-dismiss).
  const backdropDismiss = useBackdropDismiss(onClose);

  return (
    <div
      role="dialog"
      aria-modal
      aria-label={`Coding session — ${title}`}
      {...backdropDismiss}
      // `items-stretch`, not `items-start` — same reasoning as the inbox zen
      // modal (frontend/src/features/chat/inbox-chat-modal.tsx): the panel
      // sets only a maxHeight, so top-aligning it let a short session open as
      // a small card pinned to the top of the screen. This window opens
      // straight out of a chat window, so the two must frame identically.
      className="fixed inset-0 z-50 flex items-stretch justify-center"
      style={{
        padding: '56px 24px',
        background: 'rgba(20, 20, 26, 0.42)',
        backdropFilter: 'blur(10px) saturate(0.9)',
        WebkitBackdropFilter: 'blur(10px) saturate(0.9)',
        animation: 'eddy-coding-bg-in 220ms ease-out',
      }}
    >
      <style>{`
        @keyframes eddy-coding-bg-in { from { opacity: 0 } to { opacity: 1 } }
        @keyframes eddy-coding-panel-in {
          from { opacity: 0; transform: translateY(8px) }
          to   { opacity: 1; transform: translateY(0) }
        }
        /* FRONT-2478: the interview bar's halo — a soft primary ring that
           breathes so the exit reads as the live thing on screen. */
        @keyframes eddy-interview-halo {
          0%, 100% { box-shadow: 0 0 0 3px color-mix(in oklch, var(--primary) 12%, transparent) }
          50%      { box-shadow: 0 0 0 6px color-mix(in oklch, var(--primary) 6%, transparent) }
        }
        /* Same scaling contract as the inbox zen modal: the reading column
           sets the base font-size from the Aa pref; markdown rides it in em
           so EDDY's prose grows together. Chrome stays on fixed tokens. */
        .eddy-reading .markdown-body { font-size: 1em; line-height: 1.65; }
        .eddy-reading .markdown-body h1 { font-size: 1.5em; }
        .eddy-reading .markdown-body h2 { font-size: 1.28em; }
        .eddy-reading .markdown-body h3,
        .eddy-reading .markdown-body h4 { font-size: 1.12em; }
      `}</style>

      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="builder-window-panel"
        className="flex flex-col w-full min-w-0 overflow-clip bg-card text-foreground border border-border"
        style={{
          maxWidth: cardMax,
          // `100%` of the backdrop's padded box rather than a hand-computed
          // `calc(100vh - 112px)` that has to be kept in step with the padding
          // above it. The stretch does the sizing; this is the belt.
          maxHeight: '100%',
          minHeight: 420,
          borderRadius: 'var(--radius-xl-ds)',
          boxShadow: 'var(--shadow-lg)',
          animation: 'eddy-coding-panel-in 240ms ease-out',
        }}
      >
        {/* FRONT-2258: ONE 60px bar, from the Builder header design handoff.
            It replaces the old eyebrow row PLUS the tinted state strip under
            it. Two things moved rather than disappearing: the strip's state
            sentence is now the coloured lamp beside the wordmark (its hover
            card carries the words, the run's steps and its duration), and the
            strip's audience badge is now the Visibility control itself — the
            one element that both STATES who can open the app and CHANGES it,
            so the two can no longer describe it differently. */}
        <div className="flex items-center gap-3 h-[60px] px-4 shrink-0 border-b border-border">
          {/* FRONT-2379 fix-round: this crumb stays `shrink-0` end to end —
              unlike the chat window's crumb (inbox-chat-modal.tsx), it has no
              "started" badge sharing its left group to make room for, so
              there is nothing here for it to shrink away from. */}
          <div className="flex items-center gap-1.5 shrink-0" data-testid="builder-window-crumb">
            <EddyWordmark size={15} className="shrink-0 text-foreground" />
            {/* "Eddy / Builder" — what this window IS. The old "Build" pill
                said the same thing in a chip; the design states it as a path,
                which is quieter and leaves the row's weight to the lamp. */}
            <span aria-hidden className="text-[length:var(--text-md)] text-muted-foreground/50">
              /
            </span>
            <span className="text-[length:var(--text-md)] font-semibold tracking-[-0.2px] text-foreground whitespace-nowrap">
              Builder
            </span>
            {!creating && session && (
              <BuildLamp
                // FRONT-1898: display* fold in the manual-stop override, so the
                // lamp frees the instant the user clicks Stop — same reason the
                // strip took these rather than the raw tape values.
                status={displayStatus}
                reason={displayWaitingReason}
                activity={activity}
                runSummary={runSummary}
                lastActivityAt={session.lastActivityAt}
              />
            )}
          </div>
          <span className="h-5 w-px bg-border shrink-0" aria-hidden />
          {sessionOptions.length > 0 && (creating || selectedId !== null) && (
            // FRONT-341 still holds with a menu instead of a native <select>:
            // the TRIGGER renders `title` (the attached session's own name, or
            // "New project" while creating), never a list lookup — see the
            // shared switcher's docblock (FRONT-3077 moved its markup into
            // `components/builder-header` so Pro Builder renders the same one).
            //
            // FRONT-2342: no "New project" row in the menu. The standalone
            // "New" button beside this switcher is the one way to start a
            // project; a second copy one click away read as clutter.
            <HeaderProjectSwitcher
              title={title}
              options={sessionOptions.map((o) => ({ id: o.id, label: o.title }))}
              currentId={creating ? null : selectedId}
              onSelect={(id) => {
                setCreating(false);
                setSelectedId(id);
              }}
            />
          )}
          {!creating && <HeaderNewButton onClick={() => setCreating(true)} />}
          <div className="flex-1 min-w-[8px]" />
          {/* FRONT-2267: the right-hand controls are ONE group with one
              rhythm — `gap-1.5`, the same as the left cluster. They used to be
              loose children of the header row, so the row's own `gap-3` put
              12px between adjacent 34px icon buttons: enough air to read as
              unrelated items rather than one set of actions. The outer gap now
              separates the GROUPS, this one separates the icons. */}
          <div className="flex items-center gap-1.5 shrink-0">
            {!creating && session?.app && (
              <DraftUpdateButton gate={draftGate} pending={updatingDraft} onUpdate={() => void handleUpdateDraft()} />
            )}
            {/* The meter moved UP out of the strip. It is the one readout the
                design keeps permanently on screen, because it is the only number
                that changes what the user should do next (hand over, or keep
                going). */}
            {!creating && session && <ContextMeter usage={contextUsage} />}
            {/* Preview and Visibility only exist once the project is in Apps —
                before that there is no address to open and no audience to
                change, so a disabled pair would say nothing true. */}
            {!creating && session?.app && (
              // Icon only. `aria-label` is what keeps the eye a NAMED control
              // rather than an anonymous glyph — and it is the name three specs
              // address Preview by, so it must not drift now that no visible
              // word is left to agree with.
              // FRONT-3159: with both flags on, the person's own draft opens, not the live app.
              <Tip label={previewEye(session.app).label}>
                <a
                  href={previewEye(session.app).href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Preview"
                  className="size-[34px] shrink-0 inline-flex items-center justify-center rounded-[10px] border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <Eye size={16} />
                </a>
              </Tip>
            )}
            {!creating && session?.app && ownerDraftLink(session.app) && (
              <Tip label="Preview draft — your changes in a locked preview, before they go live">
                <a
                  href={ownerDraftLink(session.app)!}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Preview draft"
                  className="h-[34px] shrink-0 inline-flex items-center gap-1.5 rounded-[10px] border border-border px-2.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <Eye size={14} />
                  Preview draft
                </a>
              </Tip>
            )}
            {!creating && session && (capabilitiesFlagOn || session.app) && (
              <Tip label="Configuration — what EDDY and this app can use">
                <button
                  type="button"
                  onClick={() => setConfigOpen(true)}
                  aria-label="Configuration"
                  className="size-[34px] shrink-0 inline-flex items-center justify-center rounded-[10px] border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <SlidersHorizontalIcon size={16} />
                </button>
              </Tip>
            )}
            {!creating && session?.app && (
              // FRONT-2258: the design's "Visibility" control. Same component as
              // Apps -> Edit -> Sharing (FRONT-1880), now in its `audience`
              // trigger shape — the button READS the current audience ("Only
              // you" / "Shared" / "Public") and opens the control that changes
              // it. That is what let the state strip's separate audience badge
              // go: one element, one answer. The wrapper carries the badge's old
              // test id, since the visible words are now this button's.
              <span data-testid="coding-app-audience" className="shrink-0 inline-flex">
                <AppSharePopover
                  slug={session.app.slug}
                  audience={appAudience(session.app)}
                  scope={session.app.shareScope ?? 'workspace'}
                  people={session.app.people ?? []}
                  pausedPeople={session.app.pausedPeople ?? []}
                  listed={session.app.listedToColleagues !== false}
                  url={session.app.url}
                  audienceLock={session.app.audienceLock ?? null}
                  live={session.app.status === 'live'}
                  onChanged={refreshApp}
                  size="full"
                  // The lock alone — the glyph already distinguishes the three
                  // audiences, so the word beside it was a second copy of the
                  // same fact. Its tooltip carries the sentence instead.
                  trigger="icon"
                  className="size-[34px] rounded-[10px] px-0"
                />
              </span>
            )}
            {/* FRONT-2091: reachability, which the audience word deliberately
                does not carry. Without it an offline workspace-shared app reads
                "Shared" with nothing saying a colleague cannot open it yet. */}
            {!creating && session?.app && session.app.status !== 'live' && (
              <Tip label="This app is offline, so only you can open it right now. Its sharing settings are unchanged and apply again the moment it's live.">
                <span
                  data-testid="coding-app-offline"
                  className="shrink-0 inline-flex items-center gap-1 h-[26px] rounded-full border border-amber-500/30 bg-amber-500/10 px-2 text-[length:var(--text-xs)] text-amber-600 dark:text-amber-300"
                >
                  <EyeSlash size={12} weight="fill" aria-hidden />
                  Offline
                </span>
              </Tip>
            )}
            {/* FRONT-2940: in an editor's own draft the same place sends it to the owner for review; nothing an editor does moves the app. */}
            {!creating && session?.app && publishControlFor(session) === 'ask' && (
              <AskOwnerToAcceptButton
                slug={session.app.slug}
                ownerName={session.app.ownerName ?? "the app's owner"}
                runBusy={actionsBusy}
                className="h-[34px] rounded-[10px] px-2.5 text-[length:var(--text-sm)]"
              />
            )}
            {!creating && session && publishControlFor(session) === 'save' && (
              // FRONT-2258: the design labels this "Save"; the ACCESSIBLE NAME
              // stays "Save App" — it is what E2E-CODING-01/04/07 address this
              // control by, and E2E-CODING-04 hovers it for the exact tooltip
              // text. The markup is the shared `HeaderSaveButton` (FRONT-3077),
              // the same disc Pro Builder renders.
              <HeaderSaveButton
                onClick={handleSaveApp}
                pending={saveApp.isPending}
                disabledReason={actionsBusy ? 'Save App — available once EDDY finishes the current run' : null}
                // FRONT-1775 (spec Decision 7): every save is live now — the
                // Visibility control beside this one is what decides who can
                // open it, so the tooltip can say so directly.
                tooltip="Save App — stores this project in your Apps and makes it live at its address; who can open it is set by the Share control"
              />
            )}
            {/* FRONT-1668 review fix: this control (and the Appearance item
                inside it) must be reachable in EVERY state — creating, loading,
                error, or an attached session. Only the session-dependent items
                stay gated on `session`.

                FRONT-2267: this control carries NO tooltip. It used to, via
                the raw `Tooltip`/`TooltipTrigger` composition (`Tip` cannot
                wrap a control that is also an `asChild` trigger — FRONT-1339),
                and MP had it removed: a "…" next to a menu explains itself, and
                a hint that repeats the menu's own contents is noise on the one
                control people press most. `aria-label` stays — it is this
                button's only name. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="Session actions"
                  className="size-[34px] shrink-0 inline-flex items-center justify-center rounded-[10px] border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors active:scale-[0.96]"
                >
                  <DotsThree size={20} weight="bold" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                {!creating && session && (
                  <>
                    <Tip label="Saves this chat to your Files. Save again to update that copy." side="left">
                      <DropdownMenuItem onSelect={handleSave} disabled={save.isPending} className="gap-2">
                        <FloppyDisk size={16} />
                        Save Conversation
                      </DropdownMenuItem>
                    </Tip>
                    {/* FRONT-2258: the strip carried a copy-link button beside
                        the app's address. The one-row header has no room for
                        the address itself, so the ACTION lands here rather
                        than being dropped — Preview opens it, this copies it. */}
                    {session.app && (
                      <Tip label="Copies this app's web address to your clipboard." side="left">
                        <DropdownMenuItem onSelect={() => handleCopyAppLink(session.app!.url)} className="gap-2">
                          <LinkSimple size={16} />
                          Copy app link
                        </DropdownMenuItem>
                      </Tip>
                    )}
                    <DropdownMenuSeparator />
                    <Tip
                      label="Writes a short summary of this session so far. EDDY keeps it for Clear & continue. Your chat stays as it is."
                      side="left"
                    >
                      <DropdownMenuItem
                        onSelect={handleHandover}
                        disabled={handover.isPending || actionsBusy}
                        className="gap-2"
                      >
                        <ClipboardText size={16} />
                        Generate handover
                      </DropdownMenuItem>
                    </Tip>
                    <Tip
                      label="EDDY forgets this chat and carries on from the latest handover. Generate a handover first."
                      side="left"
                    >
                      <DropdownMenuItem
                        onSelect={handleClear}
                        disabled={clear.isPending || actionsBusy}
                        className="gap-2"
                      >
                        <Eraser size={16} />
                        Clear &amp; continue
                      </DropdownMenuItem>
                    </Tip>
                    <DropdownMenuSub>
                      <Tip
                        label="When the context meter reaches this level, EDDY writes a handover and clears its memory by itself."
                        side="top"
                      >
                        <DropdownMenuSubTrigger className="gap-2">
                          <ArrowsClockwise
                            size={16}
                            weight={autoClearChoice !== 'off' ? 'bold' : 'regular'}
                            className={autoClearChoice !== 'off' ? 'text-primary' : undefined}
                          />
                          {autoClearChoice === 'off' ? 'Auto handover off' : `Auto handover at ${autoClearChoice}%`}
                        </DropdownMenuSubTrigger>
                      </Tip>
                      <DropdownMenuSubContent>
                        <DropdownMenuRadioGroup value={autoClearChoice} onValueChange={handleAutoClearChoice}>
                          {AUTO_CLEAR_CHOICES.map((choice) => (
                            <Fragment key={choice}>
                              {/* FRONT-3301: Off is the exception, set apart after the levels. */}
                              {choice === 'off' && <DropdownMenuSeparator />}
                              <DropdownMenuRadioItem value={choice} disabled={autoClear.isPending}>
                                <span className="flex-1 tabular-nums">{autoClearChoiceLabel(choice)}</span>
                                {isDefaultAutoClearChoice(choice, session) && (
                                  <span className="ml-3 text-xs text-muted-foreground">default</span>
                                )}
                              </DropdownMenuRadioItem>
                            </Fragment>
                          ))}
                        </DropdownMenuRadioGroup>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                    <DropdownMenuSeparator />
                    {/* FRONT-1787: these are STANDING constraints — they ride
                        every turn of this session's prompt. */}
                    <Tip label="Standing rules for this project. EDDY follows them in every message." side="left">
                      <DropdownMenuItem onSelect={() => setContextOpen(true)} className="gap-2">
                        <NotePencil size={16} />
                        Project context
                      </DropdownMenuItem>
                    </Tip>
                    <Tip
                      label={
                        session.app
                          ? "Deletes this project's conversation and files in your workspace. Cannot be undone. The published app stays as it is."
                          : "Deletes this project's conversation and files in your workspace. Cannot be undone."
                      }
                      side="left"
                    >
                      <DropdownMenuItem
                        variant="destructive"
                        onSelect={() => void handleDelete()}
                        disabled={deleteSession.isPending}
                        className="gap-2"
                      >
                        <Trash size={16} />
                        Delete project
                      </DropdownMenuItem>
                    </Tip>
                    <DropdownMenuSeparator />
                  </>
                )}
                <Tip label="Sets the reading width and text size for chat views, here and in the inbox chat." side="left">
                  <DropdownMenuItem onSelect={() => setAppearanceOpen(true)} className="gap-2">
                    <TextAa size={16} />
                    Appearance
                  </DropdownMenuItem>
                </Tip>
                {/* FRONT-2267: same shape as "Auto handover" above — the menu
                    stays open on select (`preventDefault`) and a trailing
                    check marks the state, rather than introducing a second
                    toggle idiom into one menu. Unconditional, like Appearance:
                    a display preference has nothing to do with whether a
                    session is attached. */}
                <Tip label="Show or hide the short hints that appear when you hover over a control." side="left">
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      setTooltips.mutate(!tooltipsOn, {
                        onError: (err) => toast.error(`Could not save: ${(err as Error).message}`),
                      });
                    }}
                    disabled={setTooltips.isPending}
                    className="gap-2"
                  >
                    <Info size={16} weight={tooltipsOn ? 'bold' : 'regular'} className={tooltipsOn ? 'text-primary' : undefined} />
                    Tooltips
                    {tooltipsOn && <Check size={14} weight="bold" className="ml-auto text-primary" />}
                  </DropdownMenuItem>
                </Tip>
                {/* FRONT-2258: what this session has cost so far. A real
                    figure again since app building is billed (FRONT-2887),
                    always in compute blocks. Not a menu
                    ITEM — nothing here is clickable — so it is a plain block
                    at the foot of the menu, and it renders only when a run has
                    actually ended: three zeros would read as a broken
                    statistic rather than as "nothing has run yet". */}
                {!creating && session && sessionStats && (
                  <div className="mt-1 grid grid-cols-3 gap-1.5 rounded-lg bg-muted/60 p-2.5">
                    <SessionStat label="Time" value={formatSessionDuration(sessionStats.durationMs)} />
                    <SessionStat label="Turns" value={formatSessionTurns(sessionStats.turns)} />
                    <SessionStat
                      label="Cost"
                      value={formatSessionCost(sessionCost)}
                      tip={sessionCost?.estimated ? ESTIMATE_HINT : undefined}
                    />
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            {/* Rendered UNCONDITIONALLY — a sibling of the DropdownMenu, not a
                child of DropdownMenuContent, since a DropdownMenuItem unmounts
                its menu the instant it's selected, which would unmount a dialog
                nested inside it before it ever got to render. State lives on the
                modal component instead, so it survives the menu closing AND
                survives `creating`/`session` changing.

                This Dialog is inside the card's own `onClick={stopPropagation}`
                wrapper, so a click landing on its portaled content is caught
                there and never reaches the backdrop's onClose. Esc is handled by
                the capture-phase, open-dialog DOM check on the window keydown
                listener above. Both verified in a browser (@verify
                E2E-CODING-04). */}
            <Dialog open={appearanceOpen} onOpenChange={setAppearanceOpen}>
              <DialogContent className="sm:max-w-sm">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <TextAa size={16} />
                    Appearance
                  </DialogTitle>
                  <DialogDescription>
                    Choose how wide the reading column is and how large the text reads.
                  </DialogDescription>
                </DialogHeader>
                {/* FRONT-1668 review fix: `ReadingPrefsPanel` returns a bare
                    Fragment, so without this wrapper its two groups become
                    direct children of `DialogContent`'s own `grid gap-4` and
                    `last:mb-0` never fires. */}
                <div>
                  <ReadingPrefsPanel prefs={prefs} onChange={setPrefs} />
                </div>
              </DialogContent>
            </Dialog>
            {/* FRONT-1787: the project-context editor. Same sibling placement
                and same reasoning as the Appearance dialog above; keyed by
                session id so switching projects while it is open cannot carry
                one project's draft into another's file. */}
            {/* FRONT-2410 / FRONT-2473: Configuration — keyed by session id so
                a project switch never carries one session's view into another. */}
            {session && (
              <ConfigurationDialog
                // Its own key, never the bare session id: the Project context
                // dialog below is a sibling keyed by `session.id`, and two
                // siblings sharing a key made React remount this one on every
                // render — a switch snapped back and Escape no longer closed it.
                key={`configuration:${session.id}`}
                // The sessions LIST, not the attached `session`: a capability
                // save invalidates the list, while the attached row is only
                // re-read on tape events, so it goes stale after a save. But
                // `app` only rides the single-session GET (a list row leaves
                // it undefined), so it must still come from the attached row.
                session={{ ...(sessions?.find((s) => s.id === session.id) ?? session), app: session.app }}
                showBuilder={capabilitiesFlagOn}
                open={configOpen}
                onOpenChange={setConfigOpen}
              />
            )}
            {session && (
              <ProjectContextDialog
                key={session.id}
                sessionId={session.id}
                open={contextOpen}
                onOpenChange={setContextOpen}
              />
            )}
            <Tip label="Close (Esc) — EDDY keeps working in the background">
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="size-[34px] shrink-0 inline-flex items-center justify-center rounded-[10px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <X size={18} />
              </button>
            </Tip>
          </div>
        </div>

        {!creating && session?.appUnavailable && (
          <div
            data-testid="coding-app-unavailable"
            className="shrink-0 border-b border-border bg-amber-500/10 px-4 py-2 text-[length:var(--text-sm)] text-amber-600 dark:text-amber-300"
          >
            {session.appUnavailable}
          </div>
        )}

        {/* Scrolling reading column */}
        <div data-testid="build-conversation" className="relative flex-1 min-h-0 overflow-y-auto">
          {sessionsError ? (
            <CenteredNote tone="error">
              Could not load your coding projects: {(sessionsError as Error).message}
            </CenteredNote>
          ) : creating ? (
            <NewProjectView
              columnMax={columnMax}
              chatFont={chatFont}
              hasSessions={(sessions?.length ?? 0) > 0}
              onCreated={(id) => {
                setSelectedId(id);
                setCreating(false);
              }}
              onCancel={sessions && sessions.length > 0 ? () => setCreating(false) : undefined}
            />
          ) : sessionsLoading || (!session && !loadError) ? (
            <CenteredNote>
              <CircleNotch size={16} className="animate-spin" /> Loading…
            </CenteredNote>
          ) : loadError ? (
            <CenteredNote tone="error">Could not open this session: {loadError}</CenteredNote>
          ) : (
            <SessionTranscript
              columnMax={columnMax}
              chatFont={chatFont}
              title={session?.title ?? ''}
              repoPath={session?.repoPath ?? ''}
              projectSlug={session?.projectSlug ?? ''}
              transcript={transcript}
              status={status}
              queueBusy={actionsBusy}
              lastError={session?.lastError ?? null}
              paused={status === 'waiting' && waitingReason === 'paused'}
              onContinue={() =>
                send('Continue').catch((err) => {
                  toast.error(sendFailureMessage(err, 'continue'));
                })
              }
              uploads={uploads}
              projectContext={projectContext ?? null}
              onDeleteUpload={handleDeleteUpload}
              onAnswerQuestions={answerQuestions}
              stoppedBatchId={stoppedBatchId}
              displayStatus={displayStatus}
              lastActivityAt={session?.lastActivityAt}
              onStopBuild={stopRun}
            />
          )}
        </div>

        {/* Reply bar — matched to the reading column width, text-only. */}
        {!creating && !sessionsError && session && (
          <div className="border-t border-border shrink-0">
            <div className="mx-auto" style={{ maxWidth: columnMax }}>
              {interviewBar && (
                <div className="px-5 pt-3">
                  <GuidedInterviewBar
                    state={interviewBar}
                    stopping={stoppingInterview}
                    onStop={() => handleStopInterview(interviewBar.batchId, interviewBar.open)}
                  />
                </div>
              )}
              <ChatInput
                // FRONT-1036: keyed by the coding session's own id, not `sessionId`
                // (that prop also drives the chat model-mode toggle, a different
                // domain concept this composer doesn't use — see hideModelSwitch).
                draftKey={session.id}
                onSubmit={(text, attachments) => {
                  // FRONT-1308: while a run is genuinely blocked on an open
                  // card, a plain send() would just 409 (the run hasn't
                  // finished — it's blocked on this exact answer) and strand
                  // the user's typed reply. Route it into the open batch
                  // instead, same as clicking an option on the card. This
                  // includes requiresOption batches (e.g. the publish gate):
                  // FRONT-1307 dropped that card's own Other field because
                  // the gate can't treat free text as a yes, but
                  // publish-tool.ts's answer reader already handles an
                  // `other`-only answer safely — it declines to publish and
                  // relays the user's words back to the agent to confirm, so
                  // this is the one path that gets them there rather than
                  // discarding a typed reply outright.
                  //
                  // FRONT-1708: `openQuestion` here is `blockingQuestion`
                  // (use-coding-session.ts), NOT every open card — once the
                  // run has wound down (timeout or interrupt) with the card
                  // still unanswered, nothing is blocked on it any more, and
                  // typing here must start a NEW run rather than silently
                  // become the answer to a batch nobody is waiting on.
                  if (openQuestion) {
                    const answers: CodingQuestionAnswer[] = openQuestion.questions.map(() => ({
                      selected: [],
                      other: text.trim(),
                    }));
                    // FRONT-1898: rethrow after toasting, same shape as send() below.
                    return submitComposerAnswer(() => answerQuestions(openQuestion.batchId, { answers }), {
                      warning: (message) => toast.warning(message, { duration: 8000 }),
                      error: (message) => toast.error(message),
                    });
                  }
                  // FRONT-1898: rethrow after toasting so ChatInput's own
                  // submitPayload sees the rejection too and restores the
                  // typed text/attachments into the box instead of losing
                  // them — this is exactly what happened live on prod: a
                  // "coding-run limit (2)" rejection ate a real message with
                  // only a toast, no way to get the text back.
                  return send(text, attachments).catch((err) => {
                    toast.error(sendFailureMessage(err, 'composer'));
                    throw err;
                  });
                }}
                onStop={stopRun}
                isBusy={composerBusy}
                isStreaming={composerBusy}
                // BUG-037 / FRONT-850: queueBusy is the poll-backed second opinion —
                // it can un-stick the auto-fire if the SSE tape's own
                // `status` ever freezes on "working" after a dropped stream.
                // FRONT-1898: also folds in the manual-stop override, so a
                // queued draft frees the moment the user clicks Stop instead
                // of waiting on the server row to leave "running".
                queueBusy={queueBusy}
                // FRONT-1182: while the run is genuinely parked on an open
                // card, this composer's own submit IS what unblocks it (the
                // routing just above). Enter must therefore send rather than
                // queue — the queue resolves on a busy -> idle edge that
                // nothing but this submit can produce, so queueing here waits
                // on itself forever. `openQuestion` is `blockingQuestion`, so
                // a card nothing is waiting on any more does NOT set this and
                // Enter keeps queueing normally (FRONT-1708).
                submitResolvesTurn={!!openQuestion}
                // FRONT-761: hold a queued draft when the run failed.
                turnError={status === 'failed'}
                hideLiveCall
                hideImageGen
                hideModelSwitch
                placeholder={openQuestion ? 'Type your answer…' : 'What should EDDY build or change?'}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CenteredNote({ children, tone }: { children: React.ReactNode; tone?: 'error' }) {
  return (
    <div
      className={cn(
        'h-full min-h-[280px] flex items-center justify-center gap-2 p-8 text-[length:var(--text-sm)]',
        tone === 'error' ? 'text-destructive' : 'text-muted-foreground',
      )}
    >
      {children}
    </div>
  );
}

/* --------------------------- build lamp (FRONT-2258) --------------------- */

/**
 * Seconds elapsed since `startedAt`, ticking while `live` — the lamp's run
 * timer. Returns null when there is nothing to count, so the caller shows no
 * timer at all rather than a stalled "0s".
 *
 * The interval is only armed while a run is actually in flight: a finished
 * session must not keep a 1Hz re-render alive behind a window the user has
 * stopped watching. A future clock (skew between server and browser) is
 * clamped to zero rather than shown as a negative age.
 */
function useElapsedMs(startedAt: string | null | undefined, live: boolean): number | null {
  const startMs = useMemo(() => {
    if (!startedAt) return null;
    const parsed = Date.parse(startedAt);
    return Number.isFinite(parsed) ? parsed : null;
  }, [startedAt]);

  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (startMs == null || !live) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(id);
  }, [startMs, live]);

  if (startMs == null) return null;
  return Math.max(0, now - startMs);
}

/** FRONT-2721: `buildLiveness` against a clock that ticks only while a build runs. */
function useBuildLiveness(status: CodingStatus | null, lastActivityAt: string | null | undefined): BuildLiveness | null {
  const live = status === 'working' || status === 'checking';
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!live) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(id);
  }, [live]);
  return buildLiveness({ status, lastActivityAt, now });
}

/**
 * The build state, as a single coloured lamp beside the wordmark (FRONT-2258).
 *
 * This is what the tinted status strip collapsed into. The strip spent a whole
 * row stating something that is true and unchanging for most of a session's
 * life; the lamp states it in eight pixels and keeps the detail one hover
 * away — the state in words, plus what the run cost.
 *
 * What the hover card says depends on the state, deliberately:
 *  - finished cleanly -> the run's own tally ("5 steps · 5m 43s"), the only
 *    state that HAS a completed tally. `formatRunSummary` returns null for
 *    anything else, so a failed or interrupted run can never be labelled with
 *    an earlier run's numbers.
 *  - anything else -> what Eddy is doing right now and for how long, the live
 *    half the strip used to show inline.
 *
 * The ticking timer stays OUT of `aria-label`: the label would otherwise
 * change every second, and a name that churns is worse than no detail at all
 * for anyone listening to it. The state sentence is the name; the numbers
 * live in the hover card.
 */
function BuildLamp({
  status,
  reason,
  activity,
  runSummary,
  lastActivityAt,
}: {
  status: CodingStatus | null;
  reason: WaitingReason | null;
  activity: RunActivity | null;
  runSummary: RunSummary | null;
  lastActivityAt: string | null | undefined;
}) {
  const running = status === 'working' || status === 'checking';
  const elapsedMs = useElapsedMs(activity?.startedAt, running);
  const liveness = useBuildLiveness(status, lastActivityAt);
  if (!status) return null;

  const stuck = liveness?.kind === 'stuck';
  const elapsed = elapsedMs == null ? null : formatDuration(elapsedMs);
  const label = stuck ? STUCK_LABEL : buildStatusLabel(status, reason);
  // Same gate as the strip's: only `done` has a completed run behind it.
  // `waiting` means a run is in flight and blocked on the user, so quoting a
  // tally there would replace live truth with one from minutes ago.
  let meta: string | null;
  if (status === 'done') meta = formatRunSummary(runSummary);
  else if (stuck) meta = liveness?.text ?? null;
  else meta = [activity?.label, elapsed].filter(Boolean).join(' · ') || null;

  const tone = lampTone(status, reason, stuck);

  return (
    <Tip
      label={
        <span className="flex flex-col gap-1">
          <span className="font-medium">{label}</span>
          {meta && <span className="tabular-nums opacity-70">{meta}</span>}
        </span>
      }
    >
      {/* The padded box is the hover target — an 8px dot is too small to
          point at. `role="img"` so the label is announced as the graphic's
          name; a live region here would re-announce on every timer tick. */}
      <span
        data-testid="coding-build-state"
        role="img"
        aria-label={label}
        className="inline-flex items-center p-1.5 shrink-0 cursor-default"
      >
        <span
          className={cn(
            'size-2 rounded-full ring-[3px] transition-[background-color,box-shadow] duration-300',
            tone,
            running && !stuck && 'animate-pulse',
          )}
        />
      </span>
    </Tip>
  );
}

/** One cell of the "…" menu's session totals block (FRONT-2258). */
function SessionStat({ label, value, tip }: { label: string; value: ReactNode; tip?: string }) {
  return (
    <Tip label={tip}>
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-[length:var(--text-2xs)] font-medium uppercase tracking-[0.3px] text-muted-foreground">
          {label}
        </span>
        <span className="text-[length:var(--text-xs)] font-semibold tabular-nums text-foreground truncate">
          {value}
        </span>
      </div>
    </Tip>
  );
}

/* ----------------------------- new project ------------------------------ */

function NewProjectView({
  columnMax,
  chatFont,
  hasSessions,
  onCreated,
  onCancel,
}: {
  columnMax: number;
  chatFont: number;
  hasSessions: boolean;
  onCreated: (id: string) => void;
  onCancel?: () => void;
}) {
  const [name, setName] = useState('');
  // FRONT-1787: the optional upfront context.
  const [context, setContext] = useState('');
  // FRONT-2382: the optional "Design direction" section — inspiration text,
  // chosen directives, uploaded/picked reference images. Empty draft costs
  // nothing extra on submit (see buildDesignCreatePayload).
  const [design, setDesign] = useState<DesignDirectionDraft>(EMPTY_DESIGN_DRAFT);
  // FRONT-2382 review #3: lifted out of `DesignDirectionDisclosure` so the
  // Create button (and its Cmd/Ctrl+Enter shortcut below) can refuse to
  // submit while a reference image is still queued or uploading — otherwise
  // a fast submit could beat the upload and ship a project missing an image
  // that still looked staged on screen.
  const [uploading, setUploading] = useState<UploadInFlight[]>([]);
  const createGate = createGateState({ uploading: uploading.length > 0 });
  const designFlagOn = useFeatureFlag(CODING_DESIGN_DIRECTION_FLAG, false);
  const nameId = useId();
  const contextId = useId();
  const create = useCreateCodingSession();

  // FRONT-1787: the create route refuses a name this user already has a
  // project under (409 `project_exists`) and hands back the session to open
  // instead, because a repeat name reuses the same sandbox repo — so it was
  // never a new project, it was a second conversation on an existing
  // codebase. Held as state rather than read off `create.error` so the offer
  // survives the user editing the name afterwards.
  const [clash, setClash] = useState<{ sessionId: string; title: string } | null>(null);
  // FRONT-1450: app creation is builder-tier only. Gated here — the one place
  // every entry point (Apps, Home, Files, the two admin screens) funnels
  // through — rather than on each button, so a new entry point can't miss it.
  // Opening an EXISTING session stays available, matching the server, which
  // gates only create + publish.
  const { canBuildApps } = useCanBuildApps();

  const submit = useCallback(() => {
    const trimmed = name.trim();
    if (!trimmed || create.isPending || createGate.disabled) return;
    setClash(null);
    const typed = context.trim();
    // FRONT-2382: omitted whenever the flag is off or nothing was given —
    // see buildDesignCreatePayload's own doc comment for why that's byte-
    // identical to a client that never knew this section existed.
    const designPayload = buildDesignCreatePayload(design, designFlagOn);
    create.mutate(
      // Omitted when empty, which spares a plain create one sandbox
      // round-trip it has no reason to make.
      { name: trimmed, ...(typed ? { context: typed } : {}), ...(designPayload ? { design: designPayload } : {}) },
      {
        onSuccess: (data) => {
          // The context write is best-effort server-side, so a project can be
          // created without the grounding the user just typed. Saying so is
          // the whole point of the server reporting it — code review caught
          // that the field was being returned and dropped, which made the
          // route's own "no longer silent" comment untrue.
          if (data.contextWritten === false) {
            toast.warning("Project created, but EDDY didn't get your context", {
              description:
                'The workspace was busy. Add it from the “…” menu → Project context and he picks it up on his next run.',
              duration: 10000,
            });
          }
          onCreated(data.session.id);
        },
        onError: (err) => {
          const body = (err as { body?: { code?: string; sessionId?: string; title?: string } })
            .body;
          if (body?.code === 'project_exists' && body.sessionId) {
            setClash({ sessionId: body.sessionId, title: body.title ?? trimmed });
          }
        },
      },
    );
  }, [name, context, design, designFlagOn, create, onCreated, createGate.disabled]);

  if (!canBuildApps) {
    return (
      <div
        className="eddy-reading mx-auto"
        style={{ maxWidth: columnMax, padding: '44px 32px 28px', fontSize: chatFont }}
      >
        <Eyebrow label="Coding session" tag="needs access" />
        <ColumnTitle>Building apps needs a builder role</ColumnTitle>
        <p className="text-muted-foreground" style={{ margin: '0 0 26px' }}>
          Your account can use every app that&rsquo;s been shared with you, but not create new
          ones. Ask an admin to upgrade your access if you need to build.
        </p>
        {onCancel && (
          <Button variant="outline" size="sm" onClick={onCancel}>
            Back
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="eddy-reading mx-auto" style={{ maxWidth: columnMax, padding: '44px 32px 28px', fontSize: chatFont }}>
      <Eyebrow label="Coding session" tag="new project" />
      <ColumnTitle>{hasSessions ? 'Start a new project' : 'Build something with EDDY'}</ColumnTitle>

      {/* FRONT-1787: everything below shares ONE measure. The reading column
          is sized for chat prose and runs to ~180 characters at the wide
          setting — fine for a transcript, wrong for a form, and it was what
          made a 448px field under a full-bleed paragraph read as bolted on.
          One max-width for the intro AND the fields gives the whole screen a
          single left and right edge, which is most of what was ugly.

          The copy must NOT promise fewer questions — Eddy still interviews,
          he just starts further along (see GUIDED_BUILD_LINES). Promising a
          skip would be a claim the prompt deliberately refuses to honour. */}
      <div style={{ maxWidth: '35rem' }}>
        <p
          className="text-muted-foreground [text-wrap:pretty]"
          style={{ margin: '0 0 28px' }}
        >
          Name it, and if you already know what you want, tell EDDY — he starts from that
          instead of from scratch.
        </p>

        {/* Required field. Deliberately the biggest thing on the screen: it
            is the only one that gates the button, and before this the two
            fields were the same size, so nothing said which mattered
            (ux-von-restorff-emphasis). Label 6px off its field, helper 6px
            under it, 20px to the next group — a real scale, not the flat 8px
            that made the two boxes read as one broken control
            (ux-proximity-grouping). */}
        <div style={{ marginBottom: 20 }}>
          <label
            htmlFor={nameId}
            className="block text-[length:var(--text-sm)] font-medium text-foreground"
            style={{ marginBottom: 6 }}
          >
            Project name
          </label>
          <input
            id={nameId}
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              // D5: the one-keystroke path stays intact for anyone who just
              // types a name and hits Enter.
              if (e.key === 'Enter') submit();
            }}
            placeholder="Support Triage"
            disabled={create.isPending}
            className="w-full rounded-[10px] border border-border bg-transparent px-3.5 py-2.5 text-[length:var(--text-base)] text-foreground placeholder:text-muted-foreground/60 transition-[border-color,box-shadow] focus:outline-none focus:ring-[3px] focus:ring-ring/40 focus:border-ring"
          />
          <p
            className="text-[length:var(--text-xs)] text-muted-foreground"
            style={{ margin: '6px 0 0' }}
          >
            Becomes the project&rsquo;s folder in your sandbox.
          </p>
        </div>

        {/* One unconditional field. The collapsed "carried over from previous
            work" row that used to live here is gone with the collision it
            disclosed: a repeat name is now refused outright, so there is no
            shared repo to inherit from, nothing to prefill, and no debounced
            by-name lookup to lose a race against the Create click. */}
        <div style={{ marginBottom: 24 }}>
          <div
            className="flex items-baseline justify-between gap-3"
            style={{ marginBottom: 6 }}
          >
            <label
              htmlFor={contextId}
              className="text-[length:var(--text-sm)] font-medium text-foreground"
            >
              Context for EDDY
            </label>
            {/* The one thing a user needs to know before deciding whether to
                type here at all, so it sits ON the label row rather than
                buried in a placeholder. */}
            <span className="text-[length:var(--text-xs)] text-muted-foreground">
              Optional
            </span>
          </div>
          <Textarea
            id={contextId}
            value={context}
            onChange={(e) => setContext(e.target.value)}
            onKeyDown={(e) => {
              // D5: Enter is a newline here — this box exists to hold
              // paragraphs, so a stray Enter must never destroy one.
              // Cmd/Ctrl+Enter is the deliberate submit, matching the chat
              // composer's own convention.
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                submit();
              }
            }}
            maxLength={CODING_CONTEXT_MAX_CHARS}
            rows={4}
            placeholder="Who it’s for, what it must do, anything to avoid…"
            disabled={create.isPending}
            // resize-none: the base Textarea already grows with its content
            // (field-sizing-content), so the drag handle was a second,
            // uglier answer to a solved problem.
            className="resize-none rounded-[10px] shadow-none placeholder:text-muted-foreground/60 focus-visible:ring-[3px] focus-visible:ring-ring/40"
          />
          <p
            className="text-[length:var(--text-xs)] text-muted-foreground [text-wrap:pretty]"
            style={{ margin: '6px 0 0' }}
          >
            Goals, constraints, or a link whose style you like. He still asks his own
            questions &mdash; this just means he starts from what you already know.
          </p>
        </div>

        {/* FRONT-2382: collapsed by default — this is the deepest, most
            optional field on the form, one step further than the upfront
            context above it. Flag-gated at the section boundary rather than
            inside it, so an off tenant never even pays for the disclosure's
            own collapsed row. */}
        {designFlagOn && (
          <div style={{ marginBottom: 24 }}>
            <DesignDirectionDisclosure
              name={name}
              context={context}
              value={design}
              onChange={setDesign}
              uploading={uploading}
              onUploadingChange={setUploading}
              disabled={create.isPending}
            />
          </div>
        )}

        {/* Trailing edge of the form, aligned to the fields above it, so the
            group reads as finished rather than trailing off. No plus icon:
            this submits a form, it does not add an item to a list. */}
        <div className="flex items-center justify-end gap-3">
          {context.trim().length > 0 && (
            <span className="text-[length:var(--text-xs)] text-muted-foreground">
              {/* Deliberately not platform-detected: navigator.platform is
                  deprecated AND absent under the frontend suite's plain-node
                  renderer, so branching on it would crash a component test to
                  save one character. */}
              or press <kbd className="font-sans font-medium text-foreground">&#8984;/Ctrl</kbd>
              {' + '}
              <kbd className="font-sans font-medium text-foreground">Enter</kbd>
            </span>
          )}
          <Button
            onClick={submit}
            disabled={!name.trim() || create.isPending || createGate.disabled}
            tooltip={createGate.reason}
            className="gap-1.5 transition-transform active:scale-[0.96]"
          >
            {create.isPending && <CircleNotch size={14} className="animate-spin" />}
            {create.isPending ? 'Creating…' : 'Create project'}
          </Button>
        </div>
        {/* Visible text, not just a hover hint — CLAUDE.md's disabled-control
            rule (FRONT-2382 review #3): a reason a person must act on to
            unblock a stuck-looking button belongs on screen, not only under
            the pointer. */}
        {createGate.disabled && (
          <p
            className="text-[length:var(--text-xs)] text-muted-foreground"
            style={{ margin: '6px 0 0', textAlign: 'right' }}
          >
            {createGate.reason}
          </p>
        )}

        {/* A name collision is not an error to report, it is a different
            action to offer — the thing the user almost certainly wanted is
            the project they already have. Rendered instead of the red line,
            not beside it. */}
        {clash ? (
          <div
            className="rounded-[10px] border border-border bg-muted/40 p-3
                       animate-in fade-in-0 slide-in-from-top-1 duration-200 ease-out"
            style={{ marginTop: 16 }}
          >
            <p className="text-[length:var(--text-sm)] text-foreground" style={{ margin: 0 }}>
              You already have a project called &ldquo;{clash.title}&rdquo;.
            </p>
            <p
              className="text-[length:var(--text-xs)] text-muted-foreground [text-wrap:pretty]"
              style={{ margin: '4px 0 0' }}
            >
              Opening it continues that codebase, with everything EDDY has already built.
              To start something separate, pick a different name.
            </p>
            <Button
              size="sm"
              className="transition-transform active:scale-[0.96]"
              style={{ marginTop: 10 }}
              onClick={() => onCreated(clash.sessionId)}
            >
              Open &ldquo;{clash.title}&rdquo;
            </Button>
          </div>
        ) : (
          create.isError && (
            <p className="text-[length:var(--text-sm)] text-destructive" style={{ margin: '12px 0 0' }}>
              {(create.error as Error).message}
            </p>
          )
        )}

        {onCancel && (
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2"
            style={{ marginTop: 20 }}
            onClick={onCancel}
            disabled={create.isPending}
          >
            Back to current project
          </Button>
        )}
      </div>
    </div>
  );
}

/* --------------------------- reading column bits ------------------------- */

function Eyebrow({ label, tag }: { label: string; tag: string }) {
  return (
    <div className="flex items-center gap-2.5 mb-4 uppercase text-[length:var(--text-xs)] font-medium tracking-[0.08em] text-muted-foreground">
      <span className="h-px bg-primary" style={{ width: 22 }} />
      <span className="text-primary font-semibold">{label}</span>
      <span className="text-muted-foreground/60">·</span>
      <span>{tag}</span>
    </div>
  );
}

function ColumnTitle({ children }: { children: React.ReactNode }) {
  return (
    <h1
      className="font-semibold text-foreground"
      style={{ fontSize: 'var(--text-3xl)', lineHeight: 1.15, letterSpacing: '-0.012em', margin: '0 0 14px' }}
    >
      {children}
    </h1>
  );
}

/* ----------------------------- session view ----------------------------- */

export function SessionTranscript({
  columnMax,
  chatFont,
  title,
  repoPath,
  projectSlug,
  transcript,
  status,
  queueBusy,
  lastError,
  paused,
  onContinue,
  uploads,
  projectContext,
  onDeleteUpload,
  onAnswerQuestions,
  stoppedBatchId,
  displayStatus,
  lastActivityAt,
  onStopBuild,
}: {
  columnMax: number;
  chatFont: number;
  title: string;
  repoPath: string;
  projectSlug: string;
  transcript: TranscriptItem[];
  status: CodingStatus;
  queueBusy: boolean;
  lastError: string | null;
  /** FRONT-2972: the last run stopped at its step limit and can resume. */
  paused: boolean;
  onContinue: () => void;
  uploads: UploadAttachment[];
  /** FRONT-1787: the project's CODE-CONTEXT.md, or null when it has none. */
  projectContext: string | null;
  onDeleteUpload: (name: string) => void;
  onAnswerQuestions: (
    batchId: number,
    body: { answers: CodingQuestionAnswer[] } | { skip: true } | { stopGuidedBuild: true } | { dismiss: true },
  ) => Promise<unknown>;
  /** FRONT-2478: the batch the interview bar just stopped, until its tape
   *  event lands. */
  stoppedBatchId: number | null;
  /** FRONT-1898: folds in the manual-stop override, so the working line goes
   *  the instant Stop is clicked. */
  displayStatus: CodingStatus | null;
  lastActivityAt: string | null | undefined;
  onStopBuild: () => void;
}) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);
  // Scroll-follow tracks the LOCAL tape's visible status, not queueBusy — it
  // decides whether to auto-scroll to what's actually rendered on screen.
  const working = status === 'working' || status === 'checking';

  // FRONT-707: only the NEWEST question card accepts input — an older
  // unanswered card is history (its run moved on or was superseded).
  let newestQuestionSeq = 0;
  for (const item of transcript) {
    if (item.kind === 'questions') newestQuestionSeq = item.seq;
  }

  // Follow the live tail unless the user scrolled up to read something.
  // The scroll container is the modal body, found from the sentinel. Scroll
  // it directly: `scrollIntoView` also scrolls every page behind the modal.
  const itemCount = transcript.length;
  useEffect(() => {
    if (stickToBottomRef.current) scrollConversationToEnd(bottomRef.current);
  }, [itemCount, working]);

  useEffect(() => {
    const el = bottomRef.current?.closest('.overflow-y-auto');
    if (!el) return;
    const onScroll = () => {
      stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 150;
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="eddy-reading mx-auto" style={{ maxWidth: columnMax, padding: '44px 32px 28px', fontSize: chatFont }}>
      <Eyebrow label="Coding session" tag={projectSlug} />
      <ColumnTitle>{title}</ColumnTitle>

      {/* Byline strip */}
      <div
        className="flex items-center gap-2.5 flex-wrap text-[length:var(--text-base)] text-muted-foreground border-b border-border"
        style={{ paddingBottom: 26, marginBottom: 26 }}
      >
        <span className="size-[26px] rounded-lg bg-foreground text-background inline-flex items-center justify-center font-semibold text-[length:var(--text-2xs)]">
          E
        </span>
        <span>
          Built with <strong className="text-foreground font-semibold">EDDY</strong> in your sandbox
        </span>
        <span className="text-muted-foreground/60">·</span>
        <span className="font-mono text-[length:var(--text-sm)]">{repoPath}</span>
        <span className="text-muted-foreground/60">·</span>
        <span className="inline-flex items-center gap-1 rounded-full border border-primary/25 bg-primary/10 px-2 py-0.5 text-[length:var(--text-xs)] font-medium text-primary">
          <Lightning size={10} weight="fill" /> coding
        </span>
      </div>

      {/* Uploads strip (FRONT-358) — the files currently staged in the
          project's uploads folder, mirroring the inbox modal's "In context"
          chips. The × removes the file from the repo; it's disabled while
          Eddy is working so files aren't yanked out from under the run. */}
      {uploads.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" style={{ margin: '-10px 0 26px' }}>
          <span className="uppercase text-[length:var(--text-xs)] font-medium tracking-[0.08em] text-muted-foreground">
            In the project
          </span>
          {uploads.map((u) => (
            <Tip key={u.path} label={`${u.name} — saved in the project at ${u.path}`}>
              <span
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted px-2 py-1 text-xs max-w-[260px]"
              >
                <Paperclip size={12} className="shrink-0" />
                <span className="truncate">{u.name}</span>
                <Tip label={
                    queueBusy
                      ? 'Available once EDDY finishes the current run'
                      : 'Delete from the project — removes the file from the repository'
                  }>
                  <button
                    type="button"
                    className="opacity-60 hover:opacity-100 shrink-0 disabled:opacity-30"
                    disabled={queueBusy}
                    onClick={() => onDeleteUpload(u.name)}
                    aria-label={`Delete ${u.name} from the project`}
                  >
                    <X size={12} />
                  </button>
                </Tip>
              </span>
            </Tip>
          ))}
        </div>
      )}

      {/* FRONT-1787: the project's standing context, pinned above the first
          message. Without it the only evidence the field did anything is
          whether Eddy's greeting happens to sound grounded, which is not
          evidence. Styled as a system note, not a chat bubble — the user did
          not say this in the conversation.

          Labelled "Project context", never "your context": FRONT-1831 lets
          Eddy rewrite this file, at which point "yours" would be false.
          Choosing the neutral label now avoids a rename later. */}
      {projectContext && (
        <div
          className="rounded-[10px] border border-border bg-muted/40 px-3.5 py-3"
          style={{ margin: '-10px 0 26px' }}
        >
          <p
            className="uppercase text-[length:var(--text-xs)] font-medium tracking-[0.08em] text-muted-foreground"
            style={{ margin: '0 0 6px' }}
          >
            Project context
          </p>
          <p
            className="text-[length:var(--text-sm)] text-foreground/85 whitespace-pre-wrap [text-wrap:pretty]"
            style={{ margin: 0 }}
          >
            {projectContext}
          </p>
        </div>
      )}

      {transcript.length === 0 && status !== 'working' && (
        <div className="text-muted-foreground">
          <p style={{ margin: 0 }}>Tell EDDY what to build.</p>
          <p className="text-[length:var(--text-sm)]" style={{ margin: '6px 0 0' }}>
            For example: “Build me a small page that shows a counter with two buttons.”
          </p>
        </div>
      )}

      <div className="space-y-4">
        {transcript.map((item) => (
          <TranscriptRow
            key={item.seq}
            item={item}
            newestQuestionSeq={newestQuestionSeq}
            runStatus={status}
            onAnswerQuestions={onAnswerQuestions}
            stoppedBatchId={stoppedBatchId}
          />
        ))}
        {status === 'failed' && lastError && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-[length:var(--text-sm)] text-destructive">
            <Warning size={16} className="mt-0.5 shrink-0" />
            <span>{lastError} — send a new message to try again.</span>
          </div>
        )}
        {paused && (
          <div className="flex items-center gap-3 text-[length:var(--text-sm)] text-muted-foreground">
            <span>EDDY used all its steps for this message. The work so far is kept.</span>
            <Button size="sm" variant="outline" disabled={queueBusy} onClick={onContinue}>
              Continue
            </Button>
          </div>
        )}
        {/* FRONT-2937: a spent budget refuses the next turn, said in the thread the way chat says it. */}
        <BudgetBlockedCard />
        <WorkingLine
          status={displayStatus}
          lastActivityAt={lastActivityAt}
          onStop={onStopBuild}
          onKindChange={() => {
            if (stickToBottomRef.current) scrollConversationToEnd(bottomRef.current);
          }}
        />
      </div>
      <div ref={bottomRef} />
    </div>
  );
}

/** The build lamp's colour for one state. */
function lampTone(status: CodingStatus, reason: WaitingReason | null, stuck: boolean): string {
  if (stuck) return 'bg-amber-500 ring-amber-500/15';
  if (status === 'working') return 'bg-primary ring-primary/15';
  // FRONT-3084: the link already works while the background check runs.
  if (status === 'done' || status === 'checking') return 'bg-success ring-success/15';
  if (status === 'failed') return 'bg-destructive ring-destructive/15';
  // Waiting splits: the user being the blocker is something to act on (amber,
  // the app's one canonical warning tone), a deliberate stop is merely quiet.
  if (reason === 'answer') return 'bg-amber-500 ring-amber-500/15';
  return 'bg-muted-foreground/40 ring-muted-foreground/10';
}

/** FRONT-2721: the live working line, or the stuck state with its way out. */
function WorkingLine({
  status,
  lastActivityAt,
  onStop,
  onKindChange,
}: {
  status: CodingStatus | null;
  lastActivityAt: string | null | undefined;
  onStop: () => void;
  /** The stuck banner is taller than the working line, so the transcript re-follows its tail. */
  onKindChange: () => void;
}) {
  const liveness = useBuildLiveness(status, lastActivityAt);
  const kind = liveness?.kind;
  useEffect(() => {
    if (kind) onKindChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);
  if (!liveness) return null;
  if (liveness.kind === 'working') {
    return (
      <div
        data-testid="coding-working-line"
        className="flex items-center gap-2 text-[length:var(--text-sm)] text-muted-foreground tabular-nums"
      >
        <CircleNotch size={14} className="animate-spin shrink-0" /> {liveness.text}
      </div>
    );
  }
  return (
    <div
      data-testid="coding-stuck-line"
      role="status"
      className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[length:var(--text-sm)]"
    >
      <Warning size={16} className="shrink-0 text-amber-500" />
      <span className="flex-1 min-w-0">
        <span className="font-medium text-foreground">{liveness.text}</span>{' '}
        <span className="text-muted-foreground">{liveness.hint}</span>
      </span>
      <Button variant="outline" size="sm" onClick={onStop}>
        Stop build
      </Button>
    </div>
  );
}

/* ---------------------------- transcript rows ---------------------------- */

function TranscriptRow({
  item,
  newestQuestionSeq,
  runStatus,
  onAnswerQuestions,
  stoppedBatchId,
}: {
  item: TranscriptItem;
  newestQuestionSeq: number;
  /** FRONT-2106: `deriveStatus`'s verdict, threaded down so a question card
   *  can close itself once its run is dead. The card's own pending rule is a
   *  per-item predicate — the `run_failed` boundary the backend now writes
   *  sits elsewhere on the tape and is invisible to it. */
  runStatus: CodingStatus;
  onAnswerQuestions: (
    batchId: number,
    body: { answers: CodingQuestionAnswer[] } | { skip: true } | { stopGuidedBuild: true } | { dismiss: true },
  ) => Promise<unknown>;
  stoppedBatchId: number | null;
}) {
  switch (item.kind) {
    case 'user':
      // Same bubble as the zen-chat conversation (message-bubble.tsx).
      // Upload badges (FRONT-358) stay on the bubble even after a file is
      // deleted from the project — the transcript records what happened.
      return (
        <div className="flex justify-end">
          <div className="max-w-[70%] bg-primary/10 [--md-table-ground:color-mix(in_oklab,var(--primary)_10%,var(--card))] border border-primary/40 px-3.5 py-2.5 rounded-xl text-sm text-foreground">
            <MarkdownRenderer content={item.text} />
            {item.attachments.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {item.attachments.map((a) => (
                  <Tip key={a.path} label={`${a.name} — uploaded into the project at ${a.path}`}>
                    <span
                      className="inline-flex items-center gap-1 rounded-md border border-primary/25 bg-primary/10 px-1.5 py-0.5 text-[length:var(--text-xs)] text-foreground/80 max-w-[220px]"
                    >
                      <Paperclip size={11} className="shrink-0" />
                      <span className="truncate">{a.name}</span>
                    </span>
                  </Tip>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    case 'assistant':
      // FRONT-707: the build-plan beat — the assistant message opening with
      // the brief heading gets a distinct card so "here's what we're
      // building" reads as a moment, not just another paragraph.
      // FRONT-1846: `data-testid="coding-assistant-reply"` on both branches
      // is what lets a browser spec scope an assertion to Eddy's own
      // rendered prose — the collapsed <details><pre> an ActivityRow dumps
      // a tool's raw result into (below) carries no such testid, so a
      // locator scoped here can never match that hidden trace block.
      if (item.text.trimStart().startsWith('## Our build plan')) {
        return (
          <div
            data-testid="coding-assistant-reply"
            className="rounded-xl border border-primary/40 bg-primary/5 px-4 py-3"
          >
            <MarkdownRenderer content={item.text} />
          </div>
        );
      }
      // Eddy's prose reads like the document body — rides the Aa font pref.
      return (
        <div data-testid="coding-assistant-reply">
          <MarkdownRenderer content={item.text} />
        </div>
      );
    case 'activity':
      return <ActivityRow item={item} />;
    case 'questions':
      return (
        <CodingQuestionsCard
          item={item}
          interactive={item.seq === newestQuestionSeq}
          runStatus={runStatus}
          onAnswer={onAnswerQuestions}
          stoppedLocally={stoppedBatchId === item.batchId}
        />
      );
    case 'marker': {
      const tone =
        item.variant === 'finished'
          ? 'text-success border-success/30'
          : item.variant === 'failed'
            ? 'text-destructive border-destructive/30'
            : 'text-muted-foreground border-border';
      return (
        <div className="flex items-center gap-3" aria-label={item.text}>
          <span className={cn('h-px flex-1 border-t', tone)} />
          <span className={cn('text-[length:var(--text-xs)] font-medium whitespace-nowrap', tone)}>{item.text}</span>
          <span className={cn('h-px flex-1 border-t', tone)} />
        </div>
      );
    }
  }
}

function ActivityRow({ item }: { item: Extract<TranscriptItem, { kind: 'activity' }> }) {
  const detail = [item.input && `Input:\n${item.input}`, item.resultContent && `Result:\n${item.resultContent}`]
    .filter(Boolean)
    .join('\n\n');
  return (
    <details className="group rounded-md border border-border/60 bg-muted/30 text-[length:var(--text-sm)]">
      <summary className="flex items-center gap-2 px-3 py-1.5 cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden">
        {item.result === 'pending' ? (
          <CircleNotch size={13} className="animate-spin text-muted-foreground shrink-0" />
        ) : item.result === 'error' ? (
          <X size={13} className="text-destructive shrink-0" weight="bold" />
        ) : (
          <Check size={13} className="text-success shrink-0" weight="bold" />
        )}
        <span className="truncate font-mono text-[length:var(--text-xs)] text-muted-foreground group-open:text-foreground">
          {item.label}
        </span>
      </summary>
      {detail && (
        <pre className="mx-3 mb-2 mt-1 max-h-64 overflow-auto rounded bg-background/60 p-2 text-[length:var(--text-xs)] whitespace-pre-wrap break-words text-muted-foreground">
          {detail}
        </pre>
      )}
    </details>
  );
}
